import {
  GamoraFoundationNode,
  GamoraFoundationNodeArgs,
  GamoraFoundationNodeChildMap,
} from './node.js';
import { debugCreate } from '../../utils/debug.js';

const debug = debugCreate({ namespace: 'gamora:foundation:root' });

/**
 * The signals that stop a root node
 * @public
 */
export type GamoraFoundationRootNodeSignal = 'SIGTERM' | 'SIGINT';

/**
 * GamoraFoundationRootNodeArgs
 * @public
 */
export interface GamoraFoundationRootNodeArgs<
  TChildMap extends GamoraFoundationNodeChildMap<TChildMap>,
> extends GamoraFoundationNodeArgs<TChildMap> {
  /**
   * The signals that stop the root. SIGTERM and SIGINT by default; an empty
   * list for a root embedded in another process, such as a test.
   */
  signals?: GamoraFoundationRootNodeSignal[];
}

/**
 * GamoraFoundationRootNode
 *
 * The root of an application's node tree. It owns the process lifecycle the
 * applications used to copy: the registrations run once on the strand before
 * anything starts and end the process when they fail, the services start and
 * stop once in the order the application gives, and a stop signal waits on a
 * stop that is already in progress. tsx watch restarts twice when two files
 * change at once and Kubernetes may follow SIGTERM with SIGINT; neither stops
 * a service twice.
 * @class
 * @public
 */
export abstract class GamoraFoundationRootNode<
  TChildMap extends GamoraFoundationNodeChildMap<TChildMap>,
  TStartResult = void,
> extends GamoraFoundationNode<TChildMap> {
  private readonly _$signals: GamoraFoundationRootNodeSignal[];
  private _$starting: Promise<TStartResult> | null = null;
  private _$stopping: Promise<void> | null = null;

  constructor(args: GamoraFoundationRootNodeArgs<TChildMap>) {
    super(args);
    this._$signals = args.signals ?? ['SIGTERM', 'SIGINT'];

    // The registrations run after the subclass constructor completed, which
    // the strand's start on a microtask guarantees; what the application posts
    // before then, `$start()` included, queues behind them.
    this.$strand
      .post(async () => {
        await this._$initialize();
        for (const signal of this._$signals) {
          process.on(signal, this._$onSignal);
        }
        debug.log('(constructor): Initialized root.');
      })
      .catch((error: unknown) => {
        debug.log('(constructor): Unable to initialize root.', {
          output: true,
          error,
        });
        process.exit(1);
      });
    queueMicrotask(() => this.$strand.start());
  }

  /**
   * The registrations that precede the start: paths, scopes, handlers. Runs
   * once on the strand; a failure ends the process.
   */
  protected async _$initialize(): Promise<void> {
    return;
  }

  /**
   * Starts the services in the root's order
   */
  protected abstract _$start(): Promise<TStartResult>;

  /**
   * Stops the services in the root's order
   */
  protected abstract _$stop(): Promise<void>;

  /**
   * Starts once on the strand; a later call returns the first result
   */
  $start(): Promise<TStartResult> {
    this._$starting ??= this.$strand.post(async () => this._$start());
    return this._$starting;
  }

  /**
   * Stops once on the strand; a later call or signal waits on the stop in
   * progress
   */
  $stop(): Promise<void> {
    this._$stopping ??= this.$strand.post(async () => this._$stop());
    return this._$stopping;
  }

  /**
   * Removes the signal listeners
   */
  $dispose(): void {
    for (const signal of this._$signals) {
      process.off(signal, this._$onSignal);
    }
  }

  // A stop that fails leaves services that would hold the process open, so
  // the process ends then, loudly: the failure prints whatever namespaces the
  // application enabled or not.
  private readonly _$onSignal = (signal: NodeJS.Signals): void => {
    debug.log(`(onSignal): ${signal} received, stopping.`);
    this.$stop().then(
      () => debug.log('(onSignal): Stopped root.'),
      (error: unknown) => {
        debug.log('(onSignal): Unable to stop root.', { output: true, error });
        process.exit(1);
      },
    );
  };
}
